import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { getEncryptParams } from 'src/keycloak';
import { api } from 'src/services/api';
import { CORE_MS_BASE_URL } from 'src/services/customBaseQuery';
import {
  decryptResponse,
  getHeaders,
  getQueryString,
  serializeErrorResponse
} from 'src/services/utils';
import { DataApiInputParams } from 'src/types/api';
import { getParam, getPayload } from 'src/utils/encryption';

function buildMutationFormData(
  params: Record<string, any>,
  files?: Record<string, File>
): FormData {
  const formData = new FormData();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null) {
      formData.append(key, getParam(value));
    }
  });
  if (IS_ENCRYPTION_ENABLED) {
    formData.append('payload', getPayload('', getEncryptParams()));
  }
  if (files) {
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });
  }
  return formData;
}

// A deleted session's id, briefly. Deleting a session patches the cache
// immediately (see patchListUploadSessionsCache below), but the list also
// polls independently while sessions are in progress - a poll request that
// was already in flight when the delete happened can resolve afterwards
// with a response that still includes the now-deleted row. `merge` only
// upserts/appends by id, so once that stale row lands on a page beyond 0 it
// has no way to ever get cleaned back out (only a page-0 fetch fully
// replaces the cache). Filtering fresh fetch results against this set for a
// short window closes that race.
const recentlyDeletedSessionIds = new Set<string>();
const RECENTLY_DELETED_TTL_MS = 20000;

// `listUploadSessions`'s cache is paginated via a `merge` that only appends
// unseen ids per page - it never removes/updates ids already merged in from
// an earlier page. So once more than one page is loaded, invalidating the
// tag and refetching whatever page is currently active can't reconcile a
// delete/rename that happened on an earlier, already-merged page. Patch the
// cached entries for every currently loaded page directly instead.
function patchListUploadSessionsCache(
  dispatch: any,
  getState: () => any,
  updater: (draft: any) => void
) {
  const apiState = getState()[uploadSessionsApi.reducerPath];
  const keys = Object.keys(apiState?.queries ?? {}).filter((key) =>
    key.startsWith('listUploadSessions')
  );
  keys.forEach((key) => {
    const queryState = apiState.queries[key];
    if (queryState?.status === 'fulfilled' && queryState.originalArgs) {
      dispatch(
        uploadSessionsApi.util.updateQueryData(
          'listUploadSessions',
          queryState.originalArgs,
          updater
        )
      );
    }
  });
}

export const uploadSessionsApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getUploadSessionServerState: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const qs = getQueryString(arg.params);
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/server-state${
          qs ? `?${qs}` : ''
        }`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url, arg.params, true),
          ms: true,
          returnUnparsedResponse: true,
          responseHandler: (response: Response) => response.blob()
        });
        if (result.error) return serializeErrorResponse(decryptResponse(result));
        return { data: result.data };
      }
    }),
    createUploadSession: builder.mutation({
      async queryFn({ params, manifestFile }: any, _, __, fetchWithBQ) {
        const url = `${CORE_MS_BASE_URL}file/upload-sessions`;
        const formData = buildMutationFormData(
          params,
          manifestFile ? { files_manifest: manifestFile } : undefined
        );
        const result: any = await fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true,
          returnUnparsedResponse: true,
          responseHandler: (response: Response) => {
            const contentType = response.headers.get('content-type') || '';
            if (
              contentType.includes('application/msgpack') ||
              contentType.includes('application/octet-stream')
            ) {
              return response.blob();
            }
            return response.text();
          }
        });
        if (result.error) return serializeErrorResponse(decryptResponse(result));
        return { data: result.data };
      },
      invalidatesTags: ['UploadSessions']
    }),
    listUploadSessions: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const qs = getQueryString(arg.params);
        const url = `${CORE_MS_BASE_URL}file/upload-sessions${
          qs ? `?${qs}` : ''
        }`;
        return fetchWithBQ({
          url,
          ...getHeaders(url, arg.params, true),
          ms: true
        });
      },

      serializeQueryArgs: ({ queryArgs }) => {
        const { p, ps, ...rest } = queryArgs.params || {};
        return { endpointName: 'listUploadSessions', params: rest };
      },

      merge: (currentCache, newData, { arg }) => {
        const page = arg.params?.p ?? 0;
        const freshRows = (newData?.data ?? []).filter(
          (d: any) => !recentlyDeletedSessionIds.has(String(d.id))
        );
        if (page === 0) return { ...newData, data: freshRows };
        // Upsert by id (not filter-out-existing) so a polling refetch of the
        // current page refreshes already-cached rows (status/progress) instead
        // of leaving them frozen at their first-loaded values.
        const byId = new Map(
          (currentCache?.data ?? []).map((d: any) => [d.id, d])
        );
        freshRows.forEach((d: any) => byId.set(d.id, d));
        return {
          ...newData,
          data: Array.from(byId.values())
        };
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.params?.p !== previousArg?.params?.p;
      },
      providesTags: ['UploadSessions']
    }),
    getUploadSession: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const { id } = arg.params || {};
        const qs = getQueryString({});
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${id}${
          qs ? `?${qs}` : ''
        }`;
        return fetchWithBQ({
          url,
          ...getHeaders(url, {}, true),
          ms: true
        });
      },
      providesTags: (_result, _error, arg) => [
        { type: 'UploadSessions', id: arg.params?.id }
      ]
    }),
    patchUploadSession: builder.mutation({
      async queryFn(
        arg: { id: string | number; label?: string; is_permanent?: boolean },
        _,
        __,
        fetchWithBQ
      ) {
        const { id, ...body } = arg;
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${id}`;
        const formData = buildMutationFormData(body);
        return fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        const { id, ...changes } = arg;
        patchListUploadSessionsCache(dispatch, getState, (draft) => {
          if (!Array.isArray(draft?.data)) {
            return;
          }
          const index = draft.data.findIndex(
            (s: any) => String(s.id) === String(id)
          );
          if (index !== -1) {
            draft.data[index] = { ...draft.data[index], ...changes };
          }
        });
      },
      invalidatesTags: (_result, _error, arg) => [
        'UploadSessions',
        { type: 'UploadSessions', id: arg.id }
      ]
    }),
    getUploadSessionFiles: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const { id, ...rest } = arg.params || {};
        const qs = getQueryString(rest);
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${id}/files${
          qs ? `?${qs}` : ''
        }`;
        return fetchWithBQ({
          url,
          ...getHeaders(url, rest, true),
          ms: true
        });
      },

      serializeQueryArgs: ({ queryArgs }) => {
        const { p, ps, ...rest } = queryArgs.params || {};
        return { endpointName: 'getUploadSessionFiles', params: rest };
      },

      merge: (currentCache, newData, { arg }) => {
        const page = arg.params?.p ?? 0;
        if (page === 0) return newData;
        // Upsert by id (not filter-out-existing) so a polling refetch of the
        // current page refreshes already-cached rows (status/progress) instead
        // of leaving them frozen at their first-loaded values.
        const byId = new Map(
          (currentCache?.data ?? []).map((d: any) => [d.id, d])
        );
        (newData?.data ?? []).forEach((d: any) => byId.set(d.id, d));
        return {
          ...newData,
          data: Array.from(byId.values())
        };
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.params?.p !== previousArg?.params?.p;
      },
      providesTags: (_result, _error, arg) => [
        { type: 'UploadSessions', id: `files-${arg.params?.id}` }
      ]
    }),
    getUploadSessionStatus: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const { id, ...rest } = arg.params || {};
        const qs = getQueryString(rest);
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${id}/status${qs ? `?${qs}` : ''}`;
        return fetchWithBQ({
          url,
          ...getHeaders(url, rest, true),
          ms: true
        });
      }
    }),
    patchUploadSessionStatus: builder.mutation({
      async queryFn(
        arg: { id: string | number; status: 'active' | 'paused' | 'cancelled' },
        _,
        __,
        fetchWithBQ
      ) {
        const { id, status } = arg;
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${id}/status`;
        const formData = buildMutationFormData({ status });
        return fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: (_result, _error, arg) => [
        'UploadSessions',
        { type: 'UploadSessions', id: arg.id }
      ]
    }),
    resyncUploadSessionFiles: builder.mutation({
      async queryFn(
        arg: {
          id: string | number;
          manifestFile: File;
          overwrite?: boolean;
          delete_missing?: boolean;
        },
        _,
        __,
        fetchWithBQ
      ) {
        const { id, manifestFile, overwrite, delete_missing } = arg;
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${id}/files`;
        const params: Record<string, any> = {};
        if (overwrite) params.overwrite = overwrite;
        if (delete_missing) params.delete_missing = delete_missing;
        const formData = buildMutationFormData(params, {
          files_manifest: manifestFile
        });
        const result: any = await fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true,
          returnUnparsedResponse: true,
          responseHandler: (response: Response) => {
            const contentType = response.headers.get('content-type') || '';
            if (
              contentType.includes('application/msgpack') ||
              contentType.includes('application/octet-stream')
            ) {
              return response.blob();
            }
            return response.text();
          }
        });
        if (result.error) return serializeErrorResponse(decryptResponse(result));
        return { data: result.data };
      },
      invalidatesTags: (_result: any, _error: any, arg: any) => [
        'UploadSessions',
        { type: 'UploadSessions', id: arg.id }
      ]
    }),
    deleteUploadSession: builder.mutation({
      async queryFn(
        arg: { id: string | number },
        _,
        __,
        fetchWithBQ
      ) {
        const url = `${CORE_MS_BASE_URL}file/upload-sessions/${arg.id}`;
        const formData = buildMutationFormData({});
        return fetchWithBQ({
          url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        const deletedId = String(arg.id);
        // Block the id from any fetch that lands while it's still "hot" - see
        // recentlyDeletedSessionIds above for why this is needed even with
        // the immediate patch below.
        recentlyDeletedSessionIds.add(deletedId);
        setTimeout(
          () => recentlyDeletedSessionIds.delete(deletedId),
          RECENTLY_DELETED_TTL_MS
        );
        patchListUploadSessionsCache(dispatch, getState, (draft) => {
          if (!Array.isArray(draft?.data)) {
            return;
          }
          draft.data = draft.data.filter(
            (s: any) => String(s.id) !== deletedId
          );
          if (typeof draft.total_count === 'number') {
            draft.total_count = Math.max(0, draft.total_count - 1);
          }
        });
      },
      invalidatesTags: ['UploadSessions']
    })
  })
});

export const {
  useLazyGetUploadSessionServerStateQuery,
  useCreateUploadSessionMutation,
  useListUploadSessionsQuery,
  useLazyListUploadSessionsQuery,
  useGetUploadSessionQuery,
  usePatchUploadSessionMutation,
  useGetUploadSessionFilesQuery,
  useLazyGetUploadSessionStatusQuery,
  usePatchUploadSessionStatusMutation,
  useResyncUploadSessionFilesMutation,
  useDeleteUploadSessionMutation
} = uploadSessionsApi;
